import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import { FC, useCallback, useEffect, useState } from "react";
import { BehaviorSubject } from "rxjs";
import { CollectionUtil, RequestUtil } from "src/framework/commons";
import { FullWidthButton } from "src/framework/components/buttons";
import { STATUS_CODE } from "src/framework/providers/api";
import { confirmationDialog, notifierStore } from "src/framework/store";
import { useObservable } from "src/framework/store/storeUtil";
import { financiamentosProvider } from "src/providers/pagamentos/FinancimentosProvider";
import pagamentoProvider from "src/providers/pagamentos/PagamentosProvider";
import { cidades$, getBancos, getCidades, getEstados, getPaises, paises$ } from "src/store/global.store";
import { Cidade, Clientes, FinanciamentoProposta } from "src/types";
import { BRASILEIRO, DEFAULT_DATA_FINANCIAMENTO, FINANCEIRA_BV_ID, NACIONALIDADE } from "../../financiamento/constants";
import { Financiamento } from "../../financiamento/Financiamento";
import { IAvalistaRequestDom, IConfiguracaoFinanceiraDom, IPessoaFisicaConjugeRequestDom, IPropostaDadosRequestDom, TCaregoriaOcupacao, TTipoProposta } from "../../financiamento/types";
import "./styles.scss"
import moment from "moment";
import { MAX_DATE_PESSOA_FISICA, MIN_DATE_PESSOA_FISICA } from "src/constants";

interface FinanciamentoFormProperties {
    codigoVenda: string;
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

export const configuracoesFinanceira$ = new BehaviorSubject<IConfiguracaoFinanceiraDom | null>(null);
export const dadosProposta$ = new BehaviorSubject<IPropostaDadosRequestDom | null>(null);

export const errorsList$ = new BehaviorSubject<any>([])
export const profissoesList$ = new BehaviorSubject<any>([])
export const gruposAtividadeList$ = new BehaviorSubject<any>([])
export const empresaAtividadeList$ = new BehaviorSubject<any>([])
export const empresaTipoList$ = new BehaviorSubject<any>([])
export const naturezaLegalList$ = new BehaviorSubject<any>([])
export const relacionamentosList$ = new BehaviorSubject<any>([])

export const isPessoaFisica$ = new BehaviorSubject<boolean>(false)
export const isAvalista$ = new BehaviorSubject<boolean>(false)
export const isConjuge$ = new BehaviorSubject<boolean>(false)
export const tipoProposta$ = new BehaviorSubject<TTipoProposta>("fisica")
export const naturezaLegalId$ = new BehaviorSubject<number | null>(null)
export const permiteDebitoEmConta$ = new BehaviorSubject<boolean>(false)



const FinanciamentoForm: FC<FinanciamentoFormProperties> = ({
    codigoVenda,
    open,
    onClose,
    onConfirm,
}) => {

    // const [dados, setDados] = useState<IPropostaDadosRequestDom | null>(null);
    const dados = useObservable<IPropostaDadosRequestDom | null>(dadosProposta$)
    const [dadosCliente, setDadosCliente] = useState<Clientes | null>(null);
    const [tipoPessoa, setTipoPessoa] = useState<TTipoProposta>("fisica");
    const [readonly, setReadonly] = useState<boolean>(false);
    const [disabled, setDisabled] = useState<boolean>(false);
    const [disabledEnviar, setDisabledEnviar] = useState<boolean>(true);
    const [configfuracoesFinanceira, setConfiguracoesFinanceira] = useState<IConfiguracaoFinanceiraDom | null>(null);

    /** Métodos **/
    const dispose = (): void => {
        dadosProposta$.next(DEFAULT_DATA_FINANCIAMENTO)
    }

    const getDadosCliente = async (): Promise<void> => {
        const response = await pagamentoProvider.obtemClientePedido(codigoVenda);
        if (response.statusCode === STATUS_CODE.OK) {
            setDadosCliente(response?.data || null);
        }
    }

    const isPessoaFisica = (): boolean => {
        return dadosCliente?.tipo_pessoa === "fisica"
    }

    const getListaOpcoesFinanceira = async (financeiraId: number): Promise<void> => {
        if (financeiraId && financeiraId === FINANCEIRA_BV_ID) {
            return;
        }

        try {
            const responseGrupoAtividade = await pagamentoProvider.obtemGruposAtividades(financeiraId);
            if (responseGrupoAtividade.statusCode === STATUS_CODE.OK) {
                gruposAtividadeList$.next(responseGrupoAtividade?.data || [])
            }

            const responseAtividade = await pagamentoProvider.obtemEmpresaAtividades(financeiraId);
            if (responseAtividade.statusCode === STATUS_CODE.OK) {
                empresaAtividadeList$.next(responseAtividade?.data || [])
            }

            const responseNaturezaLegal = await pagamentoProvider.obtemNaturezasLegais(financeiraId);
            if (responseNaturezaLegal.statusCode === STATUS_CODE.OK) {
                naturezaLegalList$.next(responseNaturezaLegal?.data || [])
            }

            const responseTipoEmpresa = await pagamentoProvider.obtemEmpresaTipos(financeiraId);
            if (responseTipoEmpresa.statusCode === STATUS_CODE.OK) {
                empresaTipoList$.next(responseTipoEmpresa?.data || [])
            }

            const responseRelacionamentos = await pagamentoProvider.obtemRelacionamentos(financeiraId);
            if (responseRelacionamentos.statusCode === STATUS_CODE.OK) {
                relacionamentosList$.next(responseRelacionamentos?.data || [])
            }
        } catch {

        }
    }

    const getDados = async (): Promise<any> => {
        try {
            errorsList$.next([])
            const response = await financiamentosProvider.getFinanciamentoProposta(codigoVenda)
            if (response.statusCode === STATUS_CODE.OK) {
                const _data: IPropostaDadosRequestDom = response?.data?.dados_proposta
                delete (_data as any).proposta_id
                const _tipoPessoa = response?.data?.tipo_pessoa === "juridica" ? "juridica" : "fisica";
                tipoProposta$.next(_tipoPessoa);
                setTipoPessoa(_tipoPessoa);
                setDisabledEnviar(!(response?.data?.status));
                errorsList$.next(response?.data?.status)
                permiteDebitoEmConta$.next(response?.data?.dados_proposta?.permite_debito_em_conta || false)
                if (_data) {
                    const responseProfissoes = await financiamentosProvider.getProfissoesFinanceira(response.data.financeira_id);
                    const responseConfigFinanceira = await financiamentosProvider.getConfigFinanceira(response.data.financeira_id);
                    const _cidades: Cidade[] = cidades$.getValue()?.data || [];

                    if (response.statusCode === STATUS_CODE.OK) {
                        const _dadosConfigFinanceira = responseConfigFinanceira?.data?.length ? responseConfigFinanceira.data[0] : [];
                        configuracoesFinanceira$.next(CollectionUtil.merge({}, _dadosConfigFinanceira))
                        setConfiguracoesFinanceira(CollectionUtil.merge({}, _dadosConfigFinanceira))
                    }

                    if (_tipoPessoa === "juridica") {
                        if (response.data.financeira_id) {
                            await getListaOpcoesFinanceira(response.data.financeira_id);
                        }
                        
                        if (_data?.pessoa_juridica?.endereco?.cidade) {
                            const _cidade = _cidades.find(c => c.nome === _data?.pessoa_juridica?.endereco?.cidade);
                            if (_cidade) {
                                _data.pessoa_juridica.endereco.cidade = _cidade.nome
                                _data.pessoa_juridica.endereco.cidade_ibge = _cidade.id
                            }
                        }
                    }

                    if (_tipoPessoa === "fisica") {
                        // Endereço pessoa física
                        if (_data?.pessoa_fisica?.endereco?.cidade) {
                            const _cidade = _cidades.find(c => c.nome === _data?.pessoa_fisica?.endereco?.cidade);
                            if (_cidade) {
                                _data.pessoa_fisica.endereco.cidade = _cidade.nome
                                _data.pessoa_fisica.endereco.cidade_ibge = _cidade.id
                            }
                        }

                        // Endereço pessoa física conjuge
                        if (_data?.pessoa_fisica?.conjuge?.endereco?.cidade) {
                            const _cidade = _cidades.find(c => c.nome === _data?.pessoa_fisica?.endereco?.cidade);
                            if (_cidade) {
                                _data.pessoa_fisica.conjuge.endereco.cidade = _cidade.nome
                                _data.pessoa_fisica.conjuge.endereco.cidade_ibge = _cidade.id
                            }
                        }

                        // Endereço empresa
                        if (_data?.pessoa_fisica?.ocupacao) {
                            if (_data.pessoa_fisica?.ocupacao?.categoria === "empresario" && _data?.pessoa_fisica?.ocupacao?.empresa?.endereco?.cidade) {
                                const _cidade = _cidades.find(c => c.nome === _data?.pessoa_fisica?.ocupacao?.empresa?.endereco?.cidade);
                                
                                if (_cidade) {
                                    _data.pessoa_fisica.ocupacao.empresa.endereco.cidade = _cidade.nome
                                    _data.pessoa_fisica.ocupacao.empresa.endereco.cidade_ibge = _cidade.id
                                }
                            }
                        }

                        // Endereço empresa
                        if (_data?.pessoa_fisica?.conjuge?.ocupacao) {
                            if (_data?.pessoa_fisica?.conjuge?.ocupacao?.categoria === "empresario" && _data?.pessoa_fisica?.conjuge?.ocupacao?.empresa?.endereco?.cidade) {
                                const _cidade = _cidades.find(c => c.nome === _data?.pessoa_fisica?.conjuge?.ocupacao?.empresa?.endereco?.cidade);
                                
                                if (_cidade) {
                                    _data.pessoa_fisica.conjuge.ocupacao.empresa.endereco.cidade = _cidade.nome
                                    _data.pessoa_fisica.conjuge.ocupacao.empresa.endereco.cidade_ibge = _cidade.id
                                }
                            }
                        }

                    }

                    for (const _avalista of _data?.avalistas || []) {
                        // Endereço avalista
                        if (_avalista?.endereco?.cidade) {
                            const _cidade = _cidades.find(c => c.nome === _avalista?.endereco?.cidade);
                            if (_cidade) {
                                _avalista.endereco.cidade = _cidade.nome
                                _avalista.endereco.cidade_ibge = _cidade.id
                            }
                        }

                        // Endereço avalista conjuge
                        if (_avalista?.conjuge?.endereco?.cidade) {
                            const _cidade = _cidades.find(c => c.nome === _avalista?.endereco?.cidade);
                            if (_cidade) {
                                _avalista.conjuge.endereco.cidade = _cidade.nome
                                _avalista.conjuge.endereco.cidade_ibge = _cidade.id
                            }
                        }

                        // Endereço empresa
                        if (_avalista?.ocupacao) {
                            if (_avalista?.ocupacao?.categoria === "empresario" && _avalista?.ocupacao?.empresa?.endereco?.cidade) {
                                const _cidade = _cidades.find(c => c.nome === _avalista?.ocupacao?.empresa?.endereco?.cidade);
                                
                                if (_cidade) {
                                    _avalista.ocupacao.empresa.endereco.cidade = _cidade.nome
                                    _avalista.ocupacao.empresa.endereco.cidade_ibge = _cidade.id
                                }
                            }
                        }

                        if (_avalista?.conjuge?.ocupacao) {
                            if (_avalista?.conjuge?.ocupacao?.categoria === "empresario" && _avalista?.conjuge?.ocupacao?.empresa?.endereco?.cidade) {
                                const _cidade = _cidades.find(c => c.nome === _avalista?.conjuge?.ocupacao?.empresa?.endereco?.cidade);
                                
                                if (_cidade) {
                                    _avalista.conjuge.ocupacao.empresa.endereco.cidade = _cidade.nome
                                    _avalista.conjuge.ocupacao.empresa.endereco.cidade_ibge = _cidade.id
                                }
                            }
                        }
                    }

                    const _pais = paises$.getValue()?.data?.find((p: any) => p.nome.toUpperCase() === "Brasil".toUpperCase()) || null;

                    if (_pais) {
                        if (_tipoPessoa === "fisica") {
                             if (_data.pessoa_fisica?.nacionalidade === BRASILEIRO) {
                                _data.pessoa_fisica.nacionalidade_pais = _pais.id 
                            }

                            if (_data.pessoa_fisica?.conjuge?.nacionalidade === BRASILEIRO) {
                                _data.pessoa_fisica.conjuge.nacionalidade_pais = _pais.id 
                            }
                        }

                        for (const idxAvalista in _data?.avalistas) {
                            if ((_data.avalistas as any)[idxAvalista]?.nacionalidade === BRASILEIRO) {
                                (_data.avalistas as any)[idxAvalista].nacionalidade_pais = _pais.id 
                            }

                            if ((_data.avalistas as any)[idxAvalista]?.conjuge?.nacionalidade === BRASILEIRO) {
                                (_data.avalistas as any)[idxAvalista].conjuge.nacionalidade_pais = _pais.id 
                            }
                        }
                    }


                    if (responseProfissoes.statusCode === STATUS_CODE.OK) {
                        profissoesList$.next(responseProfissoes.data);
                        setTimeout(() => {
                            const _dados: IPropostaDadosRequestDom = CollectionUtil.merge(DEFAULT_DATA_FINANCIAMENTO, _data)
                            if (_dados) {

                                if (_dados.pessoa_fisica) {
                                    _dados.pessoa_fisica.conjuge = CollectionUtil.merge(DEFAULT_DATA_FINANCIAMENTO.pessoa_fisica?.conjuge, _data.pessoa_fisica?.conjuge) 
                                }

                            }
                            dadosProposta$.next(_dados);
                        }, 500)
                    }
                }
            }
        } catch (error) {
            console.log(error)
        }
    }

    const enviarProposta = async (): Promise<void> => {
        confirmationDialog.setup({
            title: "Aviso!",
            content: "Enviar proposta?",
            onClose: () => {
                confirmationDialog.close();
            },
            onConfirmation: async () => {
                const response = await financiamentosProvider.enviarProposta(codigoVenda)
                if (response.statusCode !== STATUS_CODE.OK) {
                    notifierStore.setNotifier({
                        title: "Aviso!",
                        content: "Ocorreu um erro ao enviar proposta.",
                        onClose: () => {
                            notifierStore.setOpen(false);
                        },
                        timeOut: 2500
                    })
                    notifierStore.setOpen(true);
                    return;
                }
                notifierStore.setNotifier({
                    title: "Aviso!",
                    content: "Proposta enviada com sucesso!",
                    onClose: () => {
                        notifierStore.setOpen(false);
                        confirmationDialog.close();
                        onClose();
                    },
                    timeOut: 2500
                })
                notifierStore.setOpen(true);
            },
        })
        confirmationDialog.open();

    }

    const parseSaveData = (data: IPropostaDadosRequestDom, tipo: TTipoProposta): any => {
        if (tipo === "fisica") {
            const pessoaFisica = data?.pessoa_fisica || null;

            if (pessoaFisica) {
                pessoaFisica.data_nascimento = pessoaFisica?.data_nascimento?.substring(0, 10) || null;

                if (pessoaFisica?.nome) {
                    pessoaFisica.nome = pessoaFisica?.nome?.trim() || null;
                    pessoaFisica.nome = pessoaFisica?.nome?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                }

                if (pessoaFisica?.nome_mae) {
                    pessoaFisica.nome_mae = pessoaFisica?.nome_mae?.trim() || null;
                    pessoaFisica.nome_mae = pessoaFisica?.nome_mae?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                }

                const ocupacao = pessoaFisica.ocupacao || null;
                if (ocupacao) {
                    ocupacao.telefone = ocupacao.telefone?.replace(/\D/g, "") || null;
                    ocupacao.data_inicio_ocupacao = ocupacao.data_inicio_ocupacao?.substring(0, 10) || null;
                    if (ocupacao.categoria === "empresario") {
                        const _empresa = ocupacao.empresa;
                        if (_empresa) {
                            _empresa.data_fundacao = _empresa?.data_fundacao?.substring(0, 10) || null;
                            _empresa.cnpj = _empresa.cnpj?.replace(/\D/g, "") || null;
                            _empresa.telefone = _empresa.telefone?.replace(/\D/g, "") || null;
                            _empresa.nome = _empresa?.nome?.trim() || null;
                            _empresa.nome = _empresa.nome?.replace(/[^a-zA-Z0-9-' ]/g,"");
                            const _endereco = _empresa.endereco;
                            if (_endereco) {
                                _endereco.data_inicio_endereco = _endereco?.data_inicio_endereco?.substring(0, 10) || null;
                                _endereco.complemento = _endereco?.complemento?.trim() || null;
                                _endereco.complemento = _endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                _endereco.logradouro = _endereco?.logradouro?.trim() || null;
                                _endereco.logradouro = _endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                _endereco.bairro = _endereco?.bairro?.trim() || null;
                                _endereco.bairro = _endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                _empresa.endereco = _endereco;
                            }

                        }
                        ocupacao.empresa = _empresa;
                    }

                    pessoaFisica.ocupacao = ocupacao;
                }

                const endereco = pessoaFisica.endereco || null;
                if (endereco) {
                    endereco.data_inicio_endereco = endereco?.data_inicio_endereco?.substring(0, 10) || null;
                    endereco.bairro = endereco?.bairro?.trim()?.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                    endereco.logradouro = endereco?.logradouro?.trim()?.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                    pessoaFisica.endereco = endereco;
                }

                if (pessoaFisica.estado_civil === "casado" && pessoaFisica.conjuge) {
                    const conjuge: IPessoaFisicaConjugeRequestDom = pessoaFisica.conjuge;

                    conjuge.data_nascimento = conjuge.data_nascimento?.substring(0, 10) || null;
                    conjuge.telefone = conjuge.telefone?.replace(/\D/g, "") || null;

                    conjuge.nome = conjuge?.nome?.trim() || null;
                    conjuge.nome = conjuge?.nome?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                    conjuge.nome_mae = conjuge?.nome_mae?.trim() || null;
                    conjuge.nome_mae = conjuge?.nome_mae?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');

                    const ocupacaoConjuge = conjuge.ocupacao || null
                    if (ocupacaoConjuge) {
                        ocupacaoConjuge.telefone = ocupacaoConjuge.telefone?.replace(/\D/g, "") || null;
                        ocupacaoConjuge.data_inicio_ocupacao = ocupacaoConjuge.data_inicio_ocupacao?.substring(0, 10) || null;
                        conjuge.ocupacao = ocupacaoConjuge
                    }

                    const endereco = conjuge.endereco || null;
                    if (endereco) {
                        endereco.data_inicio_endereco = endereco?.data_inicio_endereco?.substring(0, 10) || null;
                        endereco.complemento = endereco?.complemento?.trim() || null;
                        endereco.complemento = endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"");
                        endereco.logradouro = endereco?.logradouro?.trim() || null;
                        endereco.logradouro = endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                        endereco.bairro = endereco?.bairro?.trim() || null;
                        endereco.bairro = endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                        conjuge.endereco = endereco;
                    }

                    pessoaFisica.conjuge = conjuge;
                }

                if (pessoaFisica.estado_civil !== "casado") {
                    pessoaFisica.partilha_bens = null;
                }
            }

            if (data?.avalistas?.length) {
                const avalistasResult: IAvalistaRequestDom[] = [];

                for (const avalista of data.avalistas) {

                    avalista.telefone = avalista.telefone?.replace(/\D/g, "") || null;
                    avalista.data_nascimento = avalista.data_nascimento?.substring(0, 10) || null;
                    avalista.nome = avalista?.nome?.trim() || null;
                    avalista.nome = avalista?.nome?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                    avalista.nome_mae = avalista?.nome_mae?.trim() || null;
                    avalista.nome_mae = avalista?.nome_mae?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                    console.log("log aval",avalista)

                    const endereco = avalista.endereco || null;
                    if (endereco) {
                        endereco.data_inicio_endereco = endereco?.data_inicio_endereco?.substring(0, 10) || null;
                        endereco.complemento = endereco?.complemento?.trim() || null;
                        endereco.complemento = endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"");
                        endereco.logradouro = endereco?.logradouro?.trim() || null;
                        endereco.logradouro = endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                        endereco.bairro = endereco?.bairro?.trim() || null;
                        endereco.bairro = endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                        avalista.endereco = endereco;
                    }
                    
                    const _ocupacao = avalista.ocupacao;
                    if (_ocupacao) {
                        _ocupacao.data_inicio_ocupacao = _ocupacao.data_inicio_ocupacao?.substring(0, 10) || null;

                        if (_ocupacao.categoria === "empresario") {
                            const _empresa = _ocupacao.empresa;
                            if (_empresa) {
                                _empresa.data_fundacao = _empresa?.data_fundacao?.substring(0, 10) || null;
                                _empresa.cnpj = _empresa.cnpj?.replace(/\D/g, "") || null;
                                _empresa.telefone = _empresa.telefone?.replace(/\D/g, "") || null;
                                _empresa.nome = _empresa?.nome?.trim() || null;
                                _empresa.nome = _empresa.nome?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                const _endereco = _empresa.endereco;
                                if (_endereco) {
                                    _endereco.data_inicio_endereco = _endereco?.data_inicio_endereco?.substring(0, 10) || null;
                                    _endereco.complemento = _endereco?.complemento?.trim() || null;
                                    _endereco.complemento = _endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                    _endereco.logradouro = _endereco?.logradouro?.trim() || null;
                                    _endereco.logradouro = _endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                    _endereco.bairro = _endereco?.bairro?.trim() || null;
                                    _endereco.bairro = _endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                    _empresa.endereco = _endereco;
                                }
    
                            }
                            _ocupacao.empresa = _empresa;
                        }
    
                        avalista.ocupacao = _ocupacao;
                    }


                    if (avalista.estado_civil === "casado" && avalista.conjuge) {
                        const conjuge: IPessoaFisicaConjugeRequestDom = avalista.conjuge;

                        conjuge.data_nascimento = conjuge.data_nascimento?.substring(0, 10) || null;
                        conjuge.telefone = conjuge.telefone?.replace(/\D/g, "") || null;

                        conjuge.nome = conjuge?.nome?.trim() || null;
                        conjuge.nome = conjuge?.nome?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                        conjuge.nome_mae = conjuge?.nome_mae?.trim() || null;
                        conjuge.nome_mae = conjuge?.nome_mae?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');

                        const endereco = conjuge.endereco || null;
                        if (endereco) {
                            endereco.data_inicio_endereco = endereco?.data_inicio_endereco?.substring(0, 10) || null;
                            endereco.complemento = endereco.complemento?.trim() || null;
                            endereco.complemento = endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"")
                            endereco.logradouro = endereco?.logradouro?.trim() || null;
                            endereco.logradouro = endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                            endereco.bairro = endereco?.bairro?.trim() || null;
                            endereco.bairro = endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                            conjuge.endereco = endereco;
                        }

                        const _ocupacao = avalista.conjuge.ocupacao;
                        if (_ocupacao) {
                            _ocupacao.data_inicio_ocupacao = _ocupacao.data_inicio_ocupacao?.substring(0, 10) || null;

                            if (_ocupacao.categoria === "empresario") {
                                const _empresa = _ocupacao.empresa;
                                if (_empresa) {
                                    _empresa.data_fundacao = _empresa?.data_fundacao?.substring(0, 10) || null;
                                    _empresa.cnpj = _empresa.cnpj?.replace(/\D/g, "") || null;
                                    _empresa.telefone = _empresa.telefone?.replace(/\D/g, "") || null;
                                    _empresa.nome = _empresa?.nome?.trim() || null;
                                    _empresa.nome = _empresa.nome?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                    const _endereco = _empresa.endereco;
                                    if (_endereco) {
                                        _endereco.data_inicio_endereco = _endereco?.data_inicio_endereco?.substring(0, 10) || null;
                                        _endereco.complemento = _endereco?.complemento?.trim() || null;
                                        _endereco.complemento = _endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                        _endereco.logradouro = _endereco?.logradouro?.trim() || null;
                                        _endereco.logradouro = _endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                        _endereco.bairro = _endereco?.bairro?.trim() || null;
                                        _endereco.bairro = _endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                        _empresa.endereco = _endereco;
                                    }
        
                                }
                                _ocupacao.empresa = _empresa;
                            }
        
                            avalista.conjuge.ocupacao = _ocupacao;
                        }

                        avalista.conjuge = conjuge;
                    }

                    avalistasResult.push(avalista);
                }
            }

            data.pessoa_fisica = pessoaFisica;
        } else if (tipo === "juridica") {
            const pessoaJuridica = data?.pessoa_juridica || null;
            if (pessoaJuridica) {
                pessoaJuridica.data_fundacao = pessoaJuridica.data_fundacao?.substring(0, 10) || null;

                if (pessoaJuridica?.fantasia) {
                    pessoaJuridica.fantasia = pessoaJuridica?.fantasia?.trim() || null;
                    pessoaJuridica.fantasia = pessoaJuridica?.fantasia?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                }

                const endereco = pessoaJuridica.endereco || null;
                if (endereco) {
                    endereco.data_inicio_endereco = endereco?.data_inicio_endereco?.substring(0, 10) || null;
                    endereco.complemento = endereco?.complemento?.trim() || null;
                    endereco.complemento = endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"");
                    endereco.logradouro = endereco?.logradouro?.trim() || null;
                    endereco.logradouro = endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                    endereco.bairro = endereco?.bairro?.trim() || null;
                    endereco.bairro = endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                    pessoaJuridica.endereco = endereco;
                }

                if (data?.avalistas?.length) {
                    const avalistasResult: IAvalistaRequestDom[] = [];
    
                    for (const avalista of data.avalistas) {
    
                        avalista.telefone = avalista.telefone?.replace(/\D/g, "") || null;
                        avalista.data_nascimento = avalista.data_nascimento?.substring(0, 10) || null;
                        avalista.nome = avalista?.nome?.trim() || null;
                        avalista.nome = avalista?.nome?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                        avalista.nome_mae = avalista?.nome_mae?.trim() || null;
                        avalista.nome_mae = avalista?.nome_mae?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
    
                        const endereco = avalista.endereco || null;
                        if (endereco) {
                            endereco.data_inicio_endereco = endereco?.data_inicio_endereco?.substring(0, 10) || null;
                            endereco.complemento = endereco?.complemento?.trim() || null;
                            endereco.complemento = endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"");
                            endereco.logradouro = endereco?.logradouro?.trim() || null;
                            endereco.logradouro = endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                            endereco.bairro = endereco?.bairro?.trim() || null;
                            endereco.bairro = endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                            avalista.endereco = endereco;
                        }
                        
                        const _ocupacao = avalista.ocupacao;
                        if (_ocupacao) {
                            _ocupacao.data_inicio_ocupacao = _ocupacao.data_inicio_ocupacao?.substring(0, 10) || null;
                            _ocupacao.telefone = _ocupacao.telefone?.replace(/\D/g, "") || null;
                            
                            if (_ocupacao.categoria === "empresario") {
                                const _empresa = _ocupacao.empresa;
                                if (_empresa) {
                                    _empresa.data_fundacao = _empresa?.data_fundacao?.substring(0, 10) || null;
                                    _empresa.cnpj = _empresa.cnpj?.replace(/\D/g, "") || null;
                                    _empresa.telefone = _empresa.telefone?.replace(/\D/g, "") || null;
                                    _empresa.nome = _empresa?.nome?.trim() || null;
                                    _empresa.nome = _empresa.nome?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                    const _endereco = _empresa.endereco;
                                    if (_endereco) {
                                        _endereco.data_inicio_endereco = _endereco.data_inicio_endereco?.substring(0, 10) || null;
                                        _endereco.complemento = _endereco?.complemento?.trim() || null;
                                        _endereco.complemento = _endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                        _endereco.logradouro = _endereco?.logradouro?.trim() || null;
                                        _endereco.logradouro = _endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                        _endereco.bairro = _endereco?.bairro?.trim() || null;
                                        _endereco.bairro = _endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                        _empresa.endereco = _endereco;
                                    }
        
                                }
                                _ocupacao.empresa = _empresa;
                            }else if (_ocupacao.categoria === ("assalariado" || "publico")) {
                                const _empresa = _ocupacao.empresa;
                                if (_empresa) {
                                    _empresa.telefone = _empresa.telefone?.replace(/\D/g, "") || null;
                                    _empresa.nome = _empresa?.nome?.trim() || null;
                                    _empresa.nome = _empresa.nome?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                }
                                _ocupacao.empresa = _empresa;
                            }

                            avalista.ocupacao = _ocupacao
                        }
    
                        if (avalista.estado_civil === "casado" && avalista.conjuge) {
                            const conjuge: IPessoaFisicaConjugeRequestDom = avalista.conjuge;

                            if (conjuge) {
                                
                                conjuge.data_nascimento = conjuge.data_nascimento?.substring(0, 10) || null;
                                conjuge.telefone = conjuge.telefone?.replace(/\D/g, "") || null;

                                conjuge.nome = conjuge?.nome?.trim() || null;
                                conjuge.nome = conjuge?.nome?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                conjuge.nome_mae = conjuge?.nome_mae?.trim() || null;
                                conjuge.nome_mae = conjuge?.nome_mae?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
        
                                const endereco = conjuge.endereco || null;
                                if (endereco) {
                                    endereco.data_inicio_endereco = endereco?.data_inicio_endereco?.substring(0, 10) || null;
                                    endereco.complemento = endereco?.complemento?.trim() || null;
                                    endereco.complemento = endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                    endereco.logradouro = endereco?.logradouro?.trim() || null;
                                    endereco.logradouro = endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                    endereco.bairro = endereco?.bairro?.trim() || null;
                                    endereco.bairro = endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                    conjuge.endereco = endereco;
                                }
    
                                const _ocupacao = avalista.conjuge.ocupacao;
                                if (_ocupacao) {
                                    _ocupacao.data_inicio_ocupacao = _ocupacao.data_inicio_ocupacao?.substring(0, 10) || null;
                                    _ocupacao.telefone = _ocupacao.telefone?.replace(/\D/g, "") || null;
                                    
                                    if (_ocupacao.categoria === "empresario") {
                                        const _empresa = _ocupacao.empresa;
                                        if (_empresa) {
                                            _empresa.data_fundacao = _empresa?.data_fundacao?.substring(0, 10) || null;
                                            _empresa.cnpj = _empresa.cnpj?.replace(/\D/g, "") || null;
                                            _empresa.telefone = _empresa.telefone?.replace(/\D/g, "") || null;
                                            _empresa.nome = _empresa?.nome?.trim() || null;
                                            _empresa.nome = _empresa.nome?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                            const _endereco = _empresa.endereco;
                                            if (_endereco) {
                                                _endereco.data_inicio_endereco = _endereco.data_inicio_endereco?.substring(0, 10) || null;
                                                _endereco.complemento = _endereco?.complemento?.trim() || null;
                                                _endereco.complemento = _endereco?.complemento?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                                _endereco.logradouro = _endereco?.logradouro?.trim() || null;
                                                _endereco.logradouro = _endereco?.logradouro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                                _endereco.bairro = _endereco?.bairro?.trim() || null;
                                                _endereco.bairro = _endereco?.bairro?.normalize('NFD')?.replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '');
                                                _empresa.endereco = _endereco;
                                            }
                
                                        }
                                        _ocupacao.empresa = _empresa;
                                    } else if (_ocupacao.categoria === ("assalariado" || "publico")) {
                                        const _empresa = _ocupacao.empresa;
                                        if (_empresa) {
                                            _empresa.telefone = _empresa.telefone?.replace(/\D/g, "") || null;
                                            _empresa.nome = _empresa?.nome?.trim() || null;
                                            _empresa.nome = _empresa.nome?.replace(/[^a-zA-Z0-9-' ]/g,"");
                                        }
                                        _ocupacao.empresa = _empresa;
                                    }
        
                                    conjuge.ocupacao = _ocupacao
                                }
        
                                avalista.conjuge = conjuge;
                            }
                        }
    
                        avalistasResult.push(avalista);
                    }

                    data.avalistas = avalistasResult;
                }

                data.pessoa_juridica = pessoaJuridica;
            }
        }

        return data;
    }

    const nomeInvalido = (texto:string): boolean => {
        if (!texto) {
            return true;
        } else {
            const textoSemAcentos = texto?.normalize('NFD').replace(/([\u0300-\u036f]|[^0-9a-zA-Z- ])/g, '')
            const regex = /^[A-Za-z]{2,}(?:\s[A-Za-z]+)+\b$/;
            if (!regex.test(textoSemAcentos)) {
                return true;
            }
        }
        return false;
    }

    const salvar = async (enviar: boolean = false): Promise<void> => {
        try {
            if (dados) {
                let _data: IPropostaDadosRequestDom = CollectionUtil.merge({}, dados)
                const dataNascimento = _data?.pessoa_fisica?.data_nascimento
                const nomeMae = _data?.pessoa_fisica?.nome_mae || "";

                if (isPessoaFisica()) {
                    delete _data.pessoa_juridica;
                    if (_data.pessoa_fisica?.estado_civil !== "casado") {
                        delete _data.pessoa_fisica?.conjuge
                    } else if (_data.pessoa_fisica?.conjuge) {
                        const nomeMaeConjuge = _data.pessoa_fisica?.conjuge?.nome_mae || "";
                        if (nomeInvalido(nomeMaeConjuge)) {
                            errorsList$.next([...errorsList$.getValue(), "pessoa_fisica.conjuge#nome_mae"]);
                            return;
                        }
                    }

                    for (const idxAvalista in _data?.avalistas || []) {
                        if (_data.avalistas && _data.avalistas[idxAvalista]?.estado_civil !== "casado") {
                            delete _data.avalistas[idxAvalista]?.conjuge
                        } else if (_data?.avalistas?.length && _data?.avalistas[idxAvalista]?.conjuge) {
                            const nomeMaeConjugeAvalista = _data.avalistas[idxAvalista]?.conjuge?.nome_mae || "";
                            if (nomeInvalido(nomeMaeConjugeAvalista)) {
                                errorsList$.next([...errorsList$.getValue(), `avalistas#${idxAvalista}#nome_mae`]);
                                return;
                            }
                        }
                    }

                    if (dataNascimento) {
                        if (!moment(dataNascimento).isValid()) {
                            errorsList$.next([...errorsList$.getValue(), "pessoa_fisica#data_nascimento"])
                            return;
                        }

                        const years = moment().diff(dataNascimento, 'years')
                        if (years >= 0) {
                            if (years < MIN_DATE_PESSOA_FISICA) {
                                errorsList$.next([...errorsList$.getValue(), "pessoa_fisica#data_nascimento#min"])
                                return
                            }
                            
                            if (years > MAX_DATE_PESSOA_FISICA) {
                                errorsList$.next([...errorsList$.getValue(), "pessoa_fisica#data_nascimento#max"])
                                return
                            }
                        }
                    }

                    if (nomeInvalido(nomeMae)) {
                        errorsList$.next([...errorsList$.getValue(), "pessoa_fisica#nome_mae"]);
                        return;
                    }
                }

                if (!isPessoaFisica()) {
                    delete _data.pessoa_fisica;
                }

                if (enviar) {
                    _data = parseSaveData(_data, tipoPessoa);

                    const situacaoValido = _data.pessoa_juridica?.endereco?.situacao || _data.pessoa_fisica?.endereco?.situacao
                    if (!situacaoValido) {
                        errorsList$.next([...errorsList$.getValue(), "pessoa_juridica#endereco.situacao", "pessoa_fisica#endereco.situacao"])
                        return;
                    }

                    if (isPessoaFisica()) {
                        if (!dataNascimento) {
                            errorsList$.next([...errorsList$.getValue(), "pessoa_fisica#data_nascimento"])
                            return;
                        }

                        const ocupacaoComRenda: TCaregoriaOcupacao[] = ["empresario","assalariado", "autonomo", "liberal", "publico"]
                        const ocupacaoComCampoInicioDasAtividades: TCaregoriaOcupacao[] = ["empresario"]
                        
                        const recebeCampoRenda = _data.pessoa_fisica?.ocupacao?.categoria ? ocupacaoComRenda.includes(_data.pessoa_fisica?.ocupacao?.categoria) : false
                        const recebeCampoInicioDasAtividades = _data.pessoa_fisica?.ocupacao?.categoria ? ocupacaoComCampoInicioDasAtividades.includes(_data.pessoa_fisica?.ocupacao?.categoria) : false

                        if (recebeCampoRenda) {
                            if (Number(_data.pessoa_fisica?.ocupacao?.renda_mensal || 0) <= 0) {
                                errorsList$.next([...errorsList$.getValue(), "pessoa_fisica.ocupacao#renda_mensal"])
                                return;
                            }
                        }

                        if (recebeCampoInicioDasAtividades){
                            if (!_data.pessoa_fisica?.ocupacao?.data_inicio_ocupacao) {
                                errorsList$.next([...errorsList$.getValue(), "pessoa_fisica.ocupacao#data_inicio_ocupacao"])
                                return;
                            }
                        }
                    }

                    const response = await financiamentosProvider.salvarProposta(_data, codigoVenda);
                    
                    if (response.statusCode === STATUS_CODE.OK || response.statusCode === STATUS_CODE.CREATED) {
                        errorsList$.next([...response?.data?.status || []])
                        const _disabledEnviar = response?.data?.status?.length > 0
                        setDisabledEnviar(_disabledEnviar);
                        if (!_disabledEnviar) {
                            enviarProposta();
                        }
                    }
                    
                } else {
                    confirmationDialog.setup({
                        title: "Aviso!",
                        content: "Deseja salvar as informações contidas nessa proposta de financiamento?",
                        onClose: () => {
                            confirmationDialog.close();
                        },
                        onConfirmation: async () => {
                            const response = await financiamentosProvider.salvarProposta(_data, codigoVenda);
                            if (response.statusCode === STATUS_CODE.OK || response.statusCode === STATUS_CODE.CREATED) {
                                errorsList$.next([...response?.data?.status || []])
                                setDisabledEnviar(response?.data?.status?.length > 0)
                                confirmationDialog.close();
    
                                const _oldDados = CollectionUtil.merge({}, dados);
    
                                dadosProposta$.next(CollectionUtil.merge(_oldDados, (_data as any).dados_proposta));
                                const mensagem: string = `Formulário atualizado com sucesso.${response?.data?.status?.length > 0 ? " Porém, ainda existem pendências para o envio da proposta." : ""}`
    
                                notifierStore.setNotifier({
                                    title: "Aviso!",
                                    content: mensagem,
                                    onClose: () => {
                                        notifierStore.setOpen(false);
                                        
                                    },
                                    timeOut: 2500
                                });
                                notifierStore.setOpen(true)
                            }
                        }
                    })
                    
                    confirmationDialog.open();
                }
            }
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (open) {
            RequestUtil.loader({
                asyncRequest: [],
                syncRequest: [
                    getCidades(),
                    getEstados(),
                    getPaises(),
                    getBancos(),
                ],
                syncResponses: async (responses) => {
                    getDados().then(() => {
                        getDadosCliente()
                    });
                }
            })
        }

        if (!open) {
            dispose();
        }
    }, [open]);

    useEffect(() => {
        return () => {
            dispose()
        };
    }, [])
    
    const validate = useCallback(() => {
        const errorMessages: string[] = []        
        if(dados?.pessoa_fisica?.ocupacao?.categoria === "aposentadoOuPensionista"){
            const regexBeneficio = /^(?:\d{10}|\d{3}.\d{3}.\d{3}-\d)$/gm;
            const numero_beneficio = dados?.pessoa_fisica?.ocupacao?.numero_beneficio || "";
            if(!numero_beneficio){
                errorMessages.push('O campo "Número do benefício" é obrigatório!')
            }
            else if(!regexBeneficio.test(numero_beneficio)){
                errorMessages.push("O número do benefício é inválido!")
            }
        }

        if (errorMessages.length > 0) {
            errorMessages.unshift('Em relação à seção "Dados profissionais":')            
            notifierStore.setNotifier({
                title: "Aviso!",
                content: errorMessages,
                timeOut: 3500,
                onClose: () => {
                    notifierStore.setOpen(false)
                }
            })
            notifierStore.setOpen(true)

            return false
        }
        return true;
    }, [dados])

    return <div>
        {dados  && <Dialog open={open} maxWidth="md" fullWidth className="FinanciamentoForm">
            <DialogTitle>Formulário de financiamento</DialogTitle>
            <DialogContent className="form-content">
                <Financiamento
                    readonly={readonly}
                    configfuracoesFinanceira={configfuracoesFinanceira}
                    dados={dados}
                    dadosCliente={dadosCliente}
                    onChange={(_newDados: any) => {
                        const _state = CollectionUtil.merge(dados, _newDados)
                        dadosProposta$.next(_state)
                    }}
                    isValid={(_valid) => {
                        setDisabled(!_valid);
                        setDisabledEnviar(true)
                    }}
                />
            </DialogContent>

            <div className="row actions">
                <div className="X12 M4 X--order3 M--order1">
                    <FullWidthButton
                        onClick={() => {
                            onClose();
                        }}
                        text="Fechar"
                    />
                </div>
                <div className="X6 M4 X--order2 M--order2">
                    <FullWidthButton
                        disabled={readonly || disabled}
                        color="secondary"
                        variant="outlined"
                        disableElevation
                        onClick={() => {
                            if (!validate()) {
                                return;
                            }
                            salvar(false)
                        }}
                        text="Salvar"
                    />

                </div>
                <div className="X6 M4 X--order1 M--order3">
                    <FullWidthButton
                        disabled={readonly || disabledEnviar || errorsList$.getValue()?.length > 0}
                        color="primary"
                        variant="contained"
                        disableElevation
                        onClick={() => {
                            salvar(true);
                        }}
                        text="Enviar"
                    />
                </div>
            </div>
        </Dialog>}
    </div>
}

export { FinanciamentoForm }